import {useState} from 'react';
import moment from 'moment';
import {useSnackbar} from 'notistack';
import {useDummyHandoverNotes} from './useDummyHandoverNotes';
import useUpdatePrecursor, {useContactIdAndReviewFor} from './useUpdatePrecursor';
import {useReviewContext} from '../contexts/ReviewContext';
import {useRiskExternalOnly} from '../features/risk/riskQueries';
import {precursorAndHeaderFieldsType} from '../pages/PrecursorWizard/fields/precursorFields';
import {useAutoForm} from '../components/AutoForm/useAutoForm';
import validateUUID from '../utils/validateUUID';
import {ContactAttribution} from '../pages/PrecursorSummaryPage/headerFields';

interface UseDummyDataLoaderParams {
  /** Whether the form is in read-only mode */
  readOnly: boolean;
  /** Function to increment a refresh key to force component re-render */
  incrementRefreshKey: () => void;
  /** Function to call when the dummy data has been loaded */
  onCompleted?: () => void;
}

/**
 * The "who is this review for?" answer the dummy loader should send.
 *
 * `reviewFor` is derived from this answer, so it has to be resolved from what
 * we are about to send rather than from the live form: the dummy loader is
 * usually clicked (by Playwright, or by hand) straight after landing on the
 * page, when the precursor form is still empty and a freshly created review
 * has no contact — which resolves `reviewFor` to ACCOUNT and seeds a review
 * with no contacts at all. `both` is the household default; non-household
 * accounts still resolve to ACCOUNT in `getContactIdAndReviewFor`.
 */
export const getDummyReviewContact = (
  formReviewContact?: ContactAttribution | string,
  reviewContactId?: string | null
): ContactAttribution | string =>
  formReviewContact || reviewContactId || ContactAttribution.both;

export const useDummyDataLoader = ({
  readOnly,
  incrementRefreshKey,
  onCompleted,
}: UseDummyDataLoaderParams) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);

  // Get review context for contact and review details
  const {reviewDetails} = useReviewContext();
  const contact = reviewDetails?.contact;

  // get reviewFor
  const {values} = useAutoForm<precursorAndHeaderFieldsType>();
  const reviewContact = getDummyReviewContact(values?.reviewContact, contact?.id);
  const {reviewFor, contactId} = useContactIdAndReviewFor({reviewContact});

  // Get risk profile dummy data function
  const {saveDummyRiskProfile} = useRiskExternalOnly();

  // Get handover notes dummy data function
  const {handleUseDummyData: saveDummyHandoverNotes, isDummyDataLoading} =
    useDummyHandoverNotes({
      readOnly,
      incrementRefreshKey,
    });

  // Get precursor update function
  const {updateReview, loading: updateReviewLoading} = useUpdatePrecursor({
    reviewId: reviewDetails?.id || '',
    onSuccess: () => {},
    onError: (error) => {
      enqueueSnackbar(`Error updating review: ${error}`, {
        variant: 'error',
        autoHideDuration: 5000,
      });
    },
    refetchQueries: [],
  });

  /**
   * Creates a dummy precursor/review with ad-hoc review details.
   * - Sets review name to "Test ad-hoc review"
   * - Sets meeting as happened today
   * - Configures as face-to-face meeting with £5 opportunity value
   */
  const saveDummyPrecursor = async (reviewName?: string) => {
    if (readOnly || isLoading || updateReviewLoading) return;

    try {
      enqueueSnackbar('Creating dummy precursor data...', {variant: 'info'});
      // Values based on the "setHappenedToday" function from PrecursorForm
      const dummyValues: any = {
        reviewContact,
        previousCommunications: [],
        isReviewFocused: 'No',
        name: reviewName || 'Test ad-hoc review',
        meetingHappened: 'Yes',
        pastMeetingDate: moment().toDate(),
        meetingType: 'Face to face',
        opportunityValue: '5',
        reviewFor,
        contactId: validateUUID(contactId),
      };

      await updateReview(dummyValues, {inPrecursor: true});

      // enqueueSnackbar('Dummy precursor created successfully!', {
      //   variant: 'success',
      //   autoHideDuration: 5000,
      // });
    } catch (error) {
      enqueueSnackbar(
        `Failed to create dummy precursor: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          autoHideDuration: 5000,
        }
      );
      throw error;
    }
  };

  /**
   * Loads dummy precursor, risk profiles, and handover notes in parallel.
   * - Creates dummy precursor/review with ad-hoc meeting details
   * - Creates risk profiles for all contacts
   * - Loads dummy data for handover notes
   * - Shows loading and success/error notifications
   * - Executes all operations concurrently for better performance
   */
  const loadAllDummyData = async (reviewName?: string) => {
    if (readOnly || isLoading || isDummyDataLoading) return;

    setIsLoading(true);
    enqueueSnackbar('Loading all dummy data...', {variant: 'info'});

    try {
      // Run all three operations in parallel
      await Promise.all([
        saveDummyPrecursor(reviewName),
        saveDummyRiskProfile(false),
        saveDummyHandoverNotes(),
      ]);

      enqueueSnackbar('All dummy data loaded successfully!', {
        variant: 'success',
        autoHideDuration: 5000,
      });
      // onCompleted?.(); // not doing the redirect
    } catch (error) {
      enqueueSnackbar(
        `Failed to load dummy data: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        {
          variant: 'error',
          autoHideDuration: 5000,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    /** Function to trigger loading of all dummy data (risk profiles + handover notes) */
    loadAllDummyData,
    /** Boolean indicating if dummy data is currently being loaded */
    isLoading: isLoading || isDummyDataLoading || updateReviewLoading,
    /** Individual function to load only dummy risk profiles */
    saveDummyRiskProfile,
    /** Individual function to load only dummy handover notes */
    saveDummyHandoverNotes,
    /** Individual function to create a dummy precursor/review */
    saveDummyPrecursor,
  };
};
